import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/apiRouting';
import { getLinkedInRedirectUri } from '../../config/socialMediaConfig';
import './LinkedInOAuthCallback.css';

interface CallbackState {
  storeId: string;
  storeName: string;
  timestamp: number;
}

export const LinkedInOAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getIdToken, isAuthenticated } = useAuth();

  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing LinkedIn connection...');
  const [profileName, setProfileName] = useState('');
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !hasStarted) {
      setHasStarted(true);
      processLinkedInCallback();
    }
  }, [isAuthenticated, hasStarted]);

  const processLinkedInCallback = async () => {
    try {
      const authCode = searchParams.get('code');
      const stateParam = searchParams.get('state');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (errorParam) {
        throw new Error(`LinkedIn OAuth failed: ${errorParam} - ${errorDescription || ''}`);
      }

      if (!authCode || !stateParam) {
        throw new Error('Missing authorization code or state parameter from LinkedIn OAuth response');
      }

      const decodedState = JSON.parse(atob(stateParam)) as CallbackState;
      const storedState = localStorage.getItem('linkedin_oauth_state');

      if (!storedState) {
        throw new Error('Invalid OAuth state - no stored state found');
      }

      const storedParsed = JSON.parse(storedState);
      if (decodedState.storeId !== storedParsed.storeId || decodedState.storeName !== storedParsed.storeName) {
        throw new Error('Invalid OAuth state - possible security issue');
      }

      localStorage.removeItem('linkedin_oauth_state');

      setMessage('Exchanging authorization code for access token...');

      const tokenResponse = await fetch(API_ENDPOINTS.linkedinAuth(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getIdToken()}`
        },
        body: JSON.stringify({
          code: authCode,
          redirectUri: getLinkedInRedirectUri(),
          storeId: decodedState.storeId
        })
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to exchange authorization code');
      }

      const tokenData = await tokenResponse.json();

      if (!tokenData.success) {
        throw new Error(tokenData.error || 'Token exchange failed');
      }

      const profile = tokenData.profile;
      setProfileName(profile?.name || profile?.localizedName || 'LinkedIn Account');
      setStatus('success');
      setMessage('Successfully connected LinkedIn!');

      setTimeout(() => {
        navigate('/social-media');
      }, 3000);

    } catch (error) {
      console.error('LinkedIn OAuth callback error:', error);
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'An unexpected error occurred');

      setTimeout(() => {
        navigate('/social-media');
      }, 5000);
    }
  };

  const handleManualRedirect = () => {
    navigate('/social-media');
  };

  return (
    <div className="linkedin-callback">
      <div className="callback-container">
        <div className="callback-header">
          <div className="platform-icon"><i className="fab fa-linkedin-in"></i></div>
          <h1>LinkedIn Integration</h1>
        </div>

        <div className={`callback-content ${status}`}>
          {status === 'processing' && (
            <div className="processing-state">
              <div className="spinner-container">
                <div className="spinner"></div>
              </div>
              <h2>Connecting to LinkedIn</h2>
              <p>{message}</p>
            </div>
          )}

          {status === 'success' && (
            <div className="success-state">
              <div className="success-icon">[SUCCESS]</div>
              <h2>Connection Successful!</h2>
              <p>{message}</p>
              {profileName && (
                <div className="connected-profile">
                  <p>Connected as: <strong>{profileName}</strong></p>
                </div>
              )}
              <p className="redirect-note">Redirecting to Social Media Dashboard...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="error-state">
              <div className="error-icon">[ERROR]</div>
              <h2>Connection Failed</h2>
              <p>{message}</p>
              <div className="error-actions">
                <button onClick={handleManualRedirect} className="retry-btn">
                  Return to Social Media Dashboard
                </button>
              </div>
              <p className="redirect-note">Auto-redirecting in a few seconds...</p>
            </div>
          )}
        </div>

        <div className="callback-footer">
          <div className="security-note">
            <span className="shield-icon">[SECURE]</span>
            <p>Your LinkedIn connection is secure and encrypted</p>
          </div>
        </div>
      </div>
    </div>
  );
};
